import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useInventory } from '../../contexts/InventoryContext';
import { useToast } from '../../contexts/ToastContext';
import { formatStockMessage, canIncreaseQuantity } from '../../utils/stockUtils';
import AuthModal from '../Auth/AuthModal';
import { useNavigate } from 'react-router-dom';
import './Cart.css';

const Cart = ({ isOpen, onClose }) => {
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, formatPrice, cartItemKey } = useCart();
  const { isVerifiedUser } = useAuth();
  const { loadInventory, getStockForSize } = useInventory();
  const { error: toastError } = useToast();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    cartItems.forEach((item) => {
      if (item.id) loadInventory(item.id);
    });
  }, [isOpen, cartItems, loadInventory]);

  const handleIncrease = (item, key) => {
    if (!item.id || !item.size) {
      updateQuantity(key, item.quantity + 1);
      return;
    }
    const stock = getStockForSize(item.id, item.size);
    if (!canIncreaseQuantity(stock, item.quantity)) {
      toastError(formatStockMessage(stock, item.size));
      return;
    }
    updateQuantity(key, item.quantity + 1);
  };

  const handleCheckout = () => {
    if (!isVerifiedUser) {
      setShowAuthModal(true);
      return;
    }
    onClose();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="cart-overlay" onClick={onClose}>
        <div className="cart-content" onClick={(e) => e.stopPropagation()}>
          <div className="cart-header">
            <h2 className="cart-title">Carrinho</h2>
            <button className="cart-close" onClick={onClose}>×</button>
          </div>

          <div className="cart-items">
            {cartItems.length === 0 ? (
              <div className="cart-empty">
                <p>Seu carrinho está vazio</p>
              </div>
            ) : (
              cartItems.map((item) => {
                const key = item.cartKey || cartItemKey(item);
                const stock = item.id && item.size ? getStockForSize(item.id, item.size) : Infinity;
                const atMax = item.id && item.size && item.quantity >= stock;

                return (
                  <div key={key} className="cart-item">
                    <div className="cart-item-image">
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt={item.name} loading="lazy" />
                      ) : (
                        <div className="cart-item-placeholder">HAKA</div>
                      )}
                    </div>
                    <div className="cart-item-details">
                      <h3 className="cart-item-name">{item.name}</h3>
                      {item.size && (
                        <p className="cart-item-size">Tamanho: {item.size}</p>
                      )}
                      <p className="cart-item-price">{item.price}</p>
                      <div className="cart-item-controls">
                        <button
                          onClick={() => updateQuantity(key, item.quantity - 1)}
                          className="cart-item-btn"
                        >
                          -
                        </button>
                        <span className="cart-item-quantity">{item.quantity}</span>
                        <button
                          onClick={() => handleIncrease(item, key)}
                          className="cart-item-btn"
                          disabled={atMax}
                          title={atMax ? formatStockMessage(stock, item.size) : undefined}
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeFromCart(key)}
                          className="cart-item-remove"
                        >
                          Remover
                        </button>
                      </div>
                      {atMax && (
                        <p className="cart-stock-hint">{formatStockMessage(stock, item.size)}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="cart-footer">
              <div className="cart-total">
                <span>Total:</span>
                <span className="cart-total-price">
                  {formatPrice(getTotalPrice())}
                </span>
              </div>
              <button onClick={handleCheckout} className="btn btn-filled cart-checkout-btn">
                <span>Finalizar Compra</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
};

export default Cart;
